import React, { useState, useRef } from 'react';
import { Upload, X, Eye, Image as ImageIcon } from 'lucide-react';
import { uploadImage, deleteImage, resizeImage, getImagePlaceholder } from '../../services/imageService';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

const ImageUploader = ({ 
  currentImage = null, 
  currentImagePath = null,
  onImageChange, 
  onImagePathChange,
  disabled = false,
  folder = 'rewards'
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(currentImage);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setUploading(true);

    try {
      // Redimensionar imagem antes do upload para melhor performance
      let fileToUpload = file;
      try {
        const resizedFile = await resizeImage(file, 800, 600, 0.8);
        if (resizedFile) {
          fileToUpload = resizedFile;
        }
      } catch (resizeError) {
        console.warn('Erro ao redimensionar imagem, usando arquivo original:', resizeError);
        // Continuar com arquivo original se redimensionamento falhar
      }
      
      // Upload da imagem
      const result = await uploadImage(fileToUpload, folder);
      
      // Deletar imagem anterior se existir
      if (currentImagePath) {
        await deleteImage(currentImagePath);
      }

      // Atualizar estados
      setPreviewImage(result.url);
      
      // Notificar componente pai
      if (onImageChange) onImageChange(result.url);
      if (onImagePathChange) onImagePathChange(result.path);

    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (!currentImagePath) return;

    const confirmed = window.confirm('Tem certeza que deseja remover esta imagem?');
    if (!confirmed) return;

    setUploading(true);
    setError('');

    try {
      // Deletar do Storage
      await deleteImage(currentImagePath);
      
      // Limpar estados
      setPreviewImage(null);
      
      // Notificar componente pai
      if (onImageChange) onImageChange(null);
      if (onImagePathChange) onImagePathChange(null);

    } catch (err) {
      setError('Erro ao remover imagem: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewClick = () => {
    if (previewImage) {
      setShowPreview(true);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Imagem do Prêmio
      </label>

      {/* Área de Upload/Preview */}
      <div className="relative">
        {previewImage ? (
          <div className="relative group">
            <img
              src={previewImage}
              alt="Preview do prêmio"
              className="w-full h-48 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-75 transition-opacity"
              onClick={handlePreviewClick}
            />
            
            {/* Overlay com ações */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handlePreviewClick}
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                  title="Visualizar imagem"
                >
                  <Eye size={16} className="text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || uploading}
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Alterar imagem"
                >
                  <Upload size={16} className="text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={disabled || uploading}
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Remover imagem"
                >
                  <X size={16} className="text-red-600" />
                </button>
              </div>
            </div>

            {/* Indicador de loading */}
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <LoadingSpinner size="md" />
              </div>
            )}
          </div>
        ) : (
          <div 
            className={`w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <LoadingSpinner size="md" />
            ) : (
              <>
                <ImageIcon size={32} className="text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 text-center">
                  Clique para selecionar uma imagem
                  <br />
                  <span className="text-xs text-gray-500">JPG, PNG ou WebP até 5MB</span>
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Input de arquivo (oculto) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {/* Mensagem de erro */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {/* Dicas de upload */}
      <div className="text-xs text-gray-500">
        <p>• Formatos suportados: JPG, PNG, WebP</p>
        <p>• Tamanho máximo: 5MB</p>
        <p>• Recomendado: 800x600px ou superior</p>
      </div>

      {/* Modal de preview */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Visualizar Imagem"
      >
        {previewImage && (
          <div className="flex justify-center">
            <img
              src={previewImage}
              alt="Preview ampliado"
              className="max-w-full max-h-96 object-contain rounded-lg"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImageUploader;
